import { CommonModule } from '@angular/common';
import {
	Component,
	computed,
	ElementRef,
	inject,
	input,
	output,
	signal,
	ViewChild,
	WritableSignal,
} from '@angular/core';
import {
	AbstractControl,
	FormBuilder,
	FormControl,
	FormGroup,
	ReactiveFormsModule,
	ValidationErrors,
	ValidatorFn,
	Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { IRequest } from '@app/pages/cmms/interfaces/request.interfaces';
import { noWhitespaceValidator } from '@shared/validators/no-white-spaces.validator';
import { CmmsPages } from '@app/pages/cmms/state/cmms.reducer';
import { TableType } from '@app/pages/cmms/types';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { QuillEditorComponent, QuillModule } from 'ngx-quill';
import 'quill-mention/autoregister';
import { Store } from '@ngrx/store';
import { assigneeList } from '@app/layout/state/layout.selectors';
import { IMentionItem } from '@app/domain/types';
import { NgxTolgeeModule } from '@tolgee/ngx';
import { TrackPendoService } from '@app/shared/services/track-pendo.service';
import { IAssigneeUser } from '@shared/interfaces/selectable-options.interfaces';

@Component({
	selector: 'comment-input',
	templateUrl: './comment-input.component.html',
	styleUrls: ['./comment-input.component.scss'],
	imports: [
		ReactiveFormsModule,
		MatIconModule,
		MatButtonModule,
		MatMenuModule,
		CommonModule,
		MatButtonToggleModule,
		MatTooltipModule,
		QuillEditorComponent,
		QuillModule,
		NgxTolgeeModule,
	],
})
export class CommentInputComponent {
	private readonly fb = inject(FormBuilder);
	private readonly store$ = inject(Store);
	private readonly trackPendoService = inject(TrackPendoService);

	request = input<IRequest>();

	commentSubmitted = output<{
		commentText: string;
		sendAsEmail: boolean;
		mentions: string[];
	}>();
	newEntityClicked = output<{
		page: CmmsPages;
		tableType: TableType;
	}>();

	@ViewChild('commentTextInput') commentTextInput: ElementRef;
	@ViewChild(QuillEditorComponent, { static: true }) editor:
		| QuillEditorComponent
		| undefined;

	modules = {
		toolbar: false,
		mention: {
			allowedChars: /^[A-Za-z\sÅÄÖåäöÆØÅæøå]*$/,
			positioningStrategy: 'fixed',
			mentionContainerClass:
				'ql-mention-list-container nice-scroll visible-scroll colored-scroll-track',
			onSelect: (item: IMentionItem, insertItem: any) => {
				const editor = this.editor!.quillEditor;
				insertItem(item);
				editor.insertText(editor.getLength() - 1, '', 'user');
			},

			source: (searchTerm: string, renderList: any) => {
				const assigneePkAndName = this.assigneeList().map<IMentionItem>(assignee => ({
					value: `${assignee.firstName} ${assignee.lastName}`,
					id: assignee.psId,
				}));
				if (searchTerm.length === 0) {
					renderList(assigneePkAndName, searchTerm);
					return;
				}

				const matches: IMentionItem[] = assigneePkAndName.filter(assignee =>
					assignee.value.toLowerCase().includes(searchTerm.toLowerCase())
				);

				renderList(matches, searchTerm);
			},
		},
	};

	assigneeListFromStore = this.store$.selectSignal(assigneeList);

	assigneeList = computed(() => {
		return this.assigneeListFromStore().reduce((allUsers: IAssigneeUser[], org) => {
			return allUsers.concat(...org.users);
		}, []);
	});

	sendAsEmail: WritableSignal<boolean> = signal(false);
	commentInputForm: FormGroup = this.fb.group({
		textArea: new FormControl<string>('', [
			Validators.required,
			noWhitespaceValidator(),
			this.noEmptyHtmlValidator(),
		]),
	});
	formControl: AbstractControl<string, string> = this.commentInputForm.get(
		'textArea'
	) as AbstractControl<string, string>;

	submitNewComment() {
		if (this.formControl.valid) {
			const mentionIds = [...this.formControl.value.matchAll(/data-id="(.*?)"/g)].map(
				match => match[1]
			);
			if (mentionIds.length > 0) {
				this.trackPendoService.track('Mention in comment');
			}
			this.commentSubmitted.emit({
				commentText: this.formControl.value,
				sendAsEmail: this.sendAsEmail(),
				mentions: mentionIds,
			});
			this.formControl.reset();
			setTimeout(() => {
				this.sendAsEmail.set(false);
			}, 1000);
		}
	}

	trackCommentInput(evt: KeyboardEvent): void {
		if (evt.key === 'Enter' && evt.ctrlKey) {
			evt.preventDefault();
			this.submitNewComment();
		}
	}

	newEntity(page: string, tableType: string | null = null) {
		const pageEnum = CmmsPages[page as keyof typeof CmmsPages];
		const tableTypeEnum = TableType[tableType as keyof typeof TableType];
		this.newEntityClicked.emit({ page: pageEnum, tableType: tableTypeEnum });
	}

	atBtnClicked() {
		const mentionModule: { openMenu(input: string) } = this.editor!.quillEditor.getModule(
			'mention'
		) as { openMenu(input: string) };
		mentionModule.openMenu('@');
	}

	noEmptyHtmlValidator(): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			const value = control.value ?? '';
			const textOnly = value
				.replace(/<[^>]+>/g, '')
				.replace(/&nbsp;/g, '')
				.trim();

			return textOnly.length === 0 ? { emptyHtml: true } : null;
		};
	}
}
